import { getAppConfigSingleton } from "@expert/app-config";
import { useNotification } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useVerifiedCustomerStore } from "@expert/sales-tools";
import { useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { useCallCategoryStore } from "@/call-controls/hooks/callCategoryStore";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { usePartner, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { getHashedValue, removeCountryCode } from "@/shared-utils";

import { getBackupGaiaSummary, getGaiaSummary } from "../api/sessionSummaryApiClient";
import { sanitizeRemarkText } from "../sanitizeRemarkText";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { getPartnerTool } from "../utils";
import { useSessionSummaryDispatchActions } from "./hooks";
import { defaultAdditionalSummaryTimes } from "./utils";

const EMPTY_CUSTOMER: { accountNumber?: string } = {};

/**
 * Hook for session summary logic to separate from UI components
 */
export const useSummary = () => {
    const logger = useLogger({ module: "sessionSummary" });
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();
    const partner = usePartner();
    const notification = useNotification();

    const [summaryGenerated, setSummaryGenerated] = useState(false);

    const summary = useSessionSummaryStore((state) => state.summary);
    const submitted = useSessionSummaryStore((state) => state.submitted);
    const { mdn: verifiedMdn, accountNumber } = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(activeSession.id).customer || {},
    );

    const validAccountNumber =
        accountNumber !== undefined &&
        (appConfig.getPartnersConfig(partner).allowedAccountNumberLengths?.includes(accountNumber.length) ??
            EMPTY_CUSTOMER);

    const {
        requiresManualCopy,
        gaiaSummaryModel,
        sessionSummaryTimes = defaultAdditionalSummaryTimes,
    } = appConfig.getPartnersConfig(partner);

    const shouldSendSummary = !submitted && summary;

    const {
        features: { includesSendToPartnerMeta, sendSummaryFromBackend, useMdnForAccountNo },
    } = useFeatures<WorkspaceFeatures>();
    const { dispatchSessionSubmit, dispatchSessionSummaryStats } = useSessionSummaryDispatchActions();
    const { resolutionStatus, category, subcategory } = useSessionSummaryStore(
        useShallow((state) => ({
            resolutionStatus: state.resolutionStatus,
            category: state.category,
            subcategory: state.subcategory,
        })),
    );
    const taskMdn = useTaskMdn({
        blocklistedNumbers: undefined,
        blocklistedPrefixes: undefined,
    });
    const mdn = taskMdn || verifiedMdn || "";
    const extraSessionSummaryPayloadProps = appConfig.getPartnersConfig(partner).extraSessionSummaryPayloadProps;

    /** Dispatch analytics with post-call summary to EIP */
    const sendSessionSummaryToEIP = useCallback(
        async (summaryText: string, agentCallSid: string) => {
            // Guard against duplicate dispatch (e.g., component remount re-firing the
            // SessionSummary auto-send useEffect). The Zustand `submitted` flag survives
            // remounts, so the second invocation bails before reaching the dispatcher.
            if (useSessionSummaryStore.getState().submitted) {
                return;
            }

            const mdnFormatted = removeCountryCode(mdn);
            const mdnHashed = await getHashedValue(mdnFormatted);

            const sessionSummaryPayload = {
                accountNo: useMdnForAccountNo ? mdnFormatted : accountNumber,
                remarkText: sanitizeRemarkText(summaryText),
                resolutionStatus,
                ...extraSessionSummaryPayloadProps,
                ...(category ? { category } : {}),
                ...(subcategory ? { subcategory } : {}),
            };

            // Set submitted before dispatch so concurrent re-fires see the flag and bail.
            useSessionSummaryStore.getState().setSubmitted(true);
            try {
                if (!sendSummaryFromBackend) {
                    dispatchSessionSubmit({
                        agentCallSid,
                        mdn: mdnFormatted,
                        mdnHashed,
                        sendToPartner: !!includesSendToPartnerMeta,
                        sessionSummaryPayload,
                    });
                }
                notification.success("Summary sent");
            } catch (error) {
                // Roll back the optimistic submitted flag so a manual retry isn't blocked by the dedupe guard.
                useSessionSummaryStore.getState().setSubmitted(false);
                notification.error(
                    `Failed to send session summary! Copy notes manually to ${getPartnerTool(partner)}.`,
                );
                logger.warn({ error }, "Failed to send session summary!");
            }

            // dispatch session summary stats without PII so we have an analytic in OpenSearch
            dispatchSessionSummaryStats({
                agentCallSid,
                autogeneratedSummary: summaryText,
                expirationTimestamp: 0,
                gaiaSummaryModel,
                summaryWasCopiedToClipboard: false,
                summaryWasSubmittedViaPartnerTool: undefined,
                timeRanOut: false,
                update: summaryText,
                gaiaCallCategory: useCallCategoryStore.getState().callCategory,
            });
        },
        [
            accountNumber,
            category,
            dispatchSessionSubmit,
            dispatchSessionSummaryStats,
            extraSessionSummaryPayloadProps,
            gaiaSummaryModel,
            includesSendToPartnerMeta,
            logger,
            mdn,
            notification,
            partner,
            resolutionStatus,
            sendSummaryFromBackend,
            subcategory,
            useMdnForAccountNo,
        ],
    );

    const setSessionSummaryFromGaia = useCallback(
        async (setSummaryLoaded: (arg0: boolean) => void, agentCallSid?: string) => {
            const logIdentities = { agentCallSid, sessionId: activeSession.id, partner };
            logger.info(logIdentities, "Gaia summary fetch check");

            const generatedSummary = agentCallSid
                ? await getGaiaSummary(
                      logger,
                      { callSid: agentCallSid, sessionId: activeSession.id, partner },
                      gaiaSummaryModel,
                  )
                : await getBackupGaiaSummary(logger, { sessionId: activeSession.id, partner }, gaiaSummaryModel);

            if (!generatedSummary) {
                logger.warn(logIdentities, "Received empty summary from GAIA.");
            } else {
                logger.info("Received non-empty summary from GAIA.");
                useSessionSummaryStore.getState().setSessionSummary({ value: generatedSummary, autoGenerated: true });
                setSummaryGenerated(!!generatedSummary);
                void sendSessionSummaryToEIP(generatedSummary, agentCallSid ?? "");
                logger.info(logIdentities, "Sent GAIA session summary to analytics service.");
            }
            setSummaryLoaded(true);
        },
        [activeSession.id, logger, partner, gaiaSummaryModel, sendSessionSummaryToEIP],
    );

    const onDismiss = useCallback(
        async (closeModal: () => void, _agentCallSid?: string, { checkSummary = true } = {}) => {
            logger.trace("Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                notification.success("Session summary copied to clipboard");
                try {
                    await navigator.clipboard.writeText(summary);
                } catch (error) {
                    logger.warn({ error }, "Session summary not copied to clipboard");
                }
            }
        },
        [logger, shouldSendSummary, summary, notification],
    );

    const getExtraSummaryTime = useCallback(
        (canAutosend: boolean, hasSummaryOnly: boolean) => {
            if (canAutosend) {
                return sessionSummaryTimes.autosend;
            } else if (hasSummaryOnly) {
                return sessionSummaryTimes.manualCopy;
            }
            return sessionSummaryTimes.maximum;
        },
        [sessionSummaryTimes],
    );

    return {
        summaryGenerated,
        validAccountNumber,
        requiresManualCopy,
        sessionSummaryTimes,
        summary,
        setSessionSummaryFromGaia,
        onDismiss,
        getExtraSummaryTime,
    };
};
